import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { devtools } from 'zustand/middleware'
import { authApiService as authService } from '@/lib/auth-api'
import { useTenantStore } from '@/stores/tenant-store'
import { getAccessToken, setAccessToken, removeAccessToken, getRefreshToken, setRefreshToken, removeRefreshToken } from '@/lib/utils'

interface User {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  is_verified: boolean;
  is_active: boolean;
  date_joined: string;
  profile?: any;
}

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  isInitialized: boolean;
  accessToken: string | null; // Store access token in state
  refreshToken: string | null; // Store refresh token in state
  
  // Actions
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  signup: (userData: any) => Promise<any>;
  forgotPassword: (email: string) => Promise<any>;
  resetPassword: (token: string, password: string) => Promise<any>;
  refreshAuth: () => Promise<void>;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  clearAuth: () => void; // New action to clear auth state
  setTokens: (accessToken: string | null, refreshToken: string | null) => void; // New action
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        isAuthenticated: false,
        loading: false,
        isInitialized: false,
        accessToken: null,
        refreshToken: null,

        // Set tokens action
        setTokens: (accessToken: string | null, refreshToken: string | null) => {
          set({ accessToken, refreshToken });
        },

        // Clear auth state (for handling 401 errors)
        clearAuth: () => {
          console.log('AuthStore: Clearing auth state due to 401 or invalid token');
          // Clear tenant store as well
          useTenantStore.getState().clearTenant();
          set({ 
            user: null, 
            isAuthenticated: false,
            loading: false,
            isInitialized: true,
            accessToken: null,
            refreshToken: null,
          });
        },

        // Initialize auth state
        initialize: async () => {
          const state = get();
          if (state.isInitialized) {
            console.log('AuthStore: Already initialized, skipping');
            return;
          }
          console.log('AuthStore: Starting initialization');
          if (typeof window === 'undefined') {
            set({ 
              isAuthenticated: false,
              loading: false,
              isInitialized: true,
              accessToken: null,
              refreshToken: null,
            });
            return;
          }
          set({ loading: true });
          
          try {
            // Use tokens from state
            const { accessToken, refreshToken } = get();
            if (accessToken) {
              try {
                // Try to refresh the token first
                if (refreshToken) {
                  const response = await authService.refreshToken(refreshToken);
                  if (response.access) {
                    set({ accessToken: response.access });
                  }
                  if ('refresh' in response && typeof response.refresh === 'string') {
                    set({ refreshToken: response.refresh });
                  }
                }
                // Get current user info
                const userData = await authService.getCurrentUser();
                set({ 
                  user: userData, 
                  isAuthenticated: true,
                  loading: false,
                  isInitialized: true
                });
                
                // Fetch tenant data if user is authenticated
                try {
                  const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
                  const userResponse = await fetch(`${baseUrl}/api/user/tenant-info/`, {
                    method: 'GET',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${accessToken}`,
                    },
                  });
                  
                  if (userResponse.ok) {
                    const userData = await userResponse.json();
                    if (userData.tenant) {
                      console.log('✅ Tenant data fetched during initialization:', userData.tenant);
                      useTenantStore.getState().setTenant(userData.tenant);
                    }
                  }
                } catch (error) {
                  console.error('Failed to fetch tenant data during initialization:', error);
                  // Don't throw error here as auth was successful
                }
              } catch (error: any) {
                console.error('AuthStore: Failed to get current user:', error);
                if (error?.response?.status === 401 || error?.status === 401) {
                  get().clearAuth();
                } else {
                  set({ 
                    user: null, 
                    isAuthenticated: false,
                    loading: false,
                    isInitialized: true,
                    accessToken: null,
                    refreshToken: null,
                  });
                }
              }
            } else {
              // No token available, set as not authenticated
              set({ 
                isAuthenticated: false,
                loading: false,
                isInitialized: true,
                user: null,
                accessToken: null,
                refreshToken: null,
              });
            }
          } catch (error) {
            console.error('AuthStore: Initialization error:', error);
            set({ 
              isAuthenticated: false,
              loading: false,
              isInitialized: true,
              user: null,
              accessToken: null,
              refreshToken: null,
            });
          }
        },

        // Login action
        login: async (email: string, password: string) => {
          set({ loading: true });
          try {
            const response = await authService.login({ email, password });
            const accessToken = response.tokens?.access || null;
            const refreshToken = response.tokens?.refresh || null;
            set({
              accessToken,
              refreshToken,
              user: response.user,
              isAuthenticated: !!accessToken,
              loading: false,
              isInitialized: true,
            });
            
            // Fetch and set tenant data after successful login
            if (accessToken) {
              try {
                const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
                const userResponse = await fetch(`${baseUrl}/api/user/tenant-info/`, {
                  method: 'GET',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                  },
                });
                
                if (userResponse.ok) {
                  const userData = await userResponse.json();
                  if (userData.tenant) {
                    console.log('✅ Tenant data fetched after login:', userData.tenant);
                    useTenantStore.getState().setTenant(userData.tenant);
                  }
                }
              } catch (error) {
                console.error('Failed to fetch tenant data after login:', error);
                // Don't throw error here as login was successful
              }
            }
            
            return response;
          } catch (error) {
            set({ 
              user: null, 
              isAuthenticated: false,
              loading: false,
              accessToken: null,
              refreshToken: null,
            });
            throw error;
          }
        },

        // Logout action
        logout: async () => {
          set({ loading: true });
          try {
            const { refreshToken } = get();
            if (refreshToken) {
              await authService.logout(refreshToken);
            }
          } catch (error) {
            console.error('AuthStore: Logout error:', error);
          } finally {
            set({ 
              user: null, 
              isAuthenticated: false,
              loading: false,
              accessToken: null,
              refreshToken: null,
            });
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }
          }
        },

        // Signup action
        signup: async (userData: any) => {
          set({ loading: true });
          try {
            const response = await authService.signup(userData);
            // Handle tenant information from signup response
            if (response.tenant) {
              // Convert auth API tenant to tenant store format
              const tenantData = {
                id: response.tenant.id,
                name: response.tenant.name,
                slug: response.tenant.slug,
                domain: null,
                plan_type: (response.tenant.plan_type || 'FREE') as 'FREE' | 'BASIC' | 'PRO' | 'ENTERPRISE',
                is_active: true,
                is_trial: false,
                trial_ends_at: null,
                max_users: 10,
                max_products: 1000,
                max_storage_mb: 100,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                onboarding_status: 'registration' as const,
                onboarding_completed_at: null,
                onboarding_progress_percentage: 0,
              };
              useTenantStore.getState().setTenant(tenantData);
            }
            return response;
          } catch (error) {
            throw error;
          } finally {
            set({ loading: false });
          }
        },

        // Forgot password action
        forgotPassword: async (email: string) => {
          set({ loading: true });
          try {
            const response = await authService.forgotPassword(email);
            return response;
          } catch (error) {
            throw error;
          } finally {
            set({ loading: false });
          }
        },

        // Reset password action
        resetPassword: async (token: string, password: string) => {
          set({ loading: true });
          try {
            const response = await authService.resetPassword(token, password);
            return response;
          } catch (error) {
            throw error;
          } finally {
            set({ loading: false });
          }
        },

        // Refresh auth action
        refreshAuth: async () => {
          try {
            const { refreshToken } = get();
            if (refreshToken) {
              const response = await authService.refreshToken(refreshToken);
              if (response.access) {
                set({ accessToken: response.access });
              }
              if ('refresh' in response && typeof response.refresh === 'string') {
                set({ refreshToken: response.refresh });
              }
            }
            // After refreshing, check if accessToken is present
            const { accessToken } = get();
            if (!accessToken) {
              get().clearAuth();
            }
          } catch (error: any) {
            if (error?.response?.status === 401 || error?.status === 401) {
              get().clearAuth();
            } else {
              set({ 
                user: null, 
                isAuthenticated: false,
                accessToken: null,
                refreshToken: null,
              });
            }
          }
        },

        // Set user action
        setUser: (user: User | null) => {
          set({ user });
        },

        // Set loading action
        setLoading: (loading: boolean) => {
          set({ loading });
        },

        // Set initialized action
        setInitialized: (isInitialized: boolean) => {
          set({ isInitialized });
        },
      }),
      {
        name: 'auth-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: !!state.accessToken, // isAuthenticated is true only if accessToken exists
          isInitialized: state.isInitialized,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
        }),
        version: 1,
        onRehydrateStorage: () => (state) => {
          console.log('AuthStore: Rehydrated from storage:', state);
        },
      }
    ),
    {
      name: 'auth-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
) 